import { Injectable, computed, signal } from '@angular/core';

/** The cookie/tracking categories the user can consent to. `necessary` is always
 *  on (auth/session) and isn't represented here. */
export interface ConsentState {
  /** Error + performance monitoring (Application Insights, Clarity if ever added). */
  analytics: boolean;
  /** Advertising/measurement (Meta Pixel). */
  marketing: boolean;
  /** True once the user has made an explicit choice (hides the first-run banner). */
  decided:   boolean;
}

const STORAGE_KEY = 'emitroll.cookie-consent.v1';
const EMPTY: ConsentState = { analytics: false, marketing: false, decided: false };

/**
 * Single source of truth for cookie/tracking consent. Trackers (Meta Pixel, and
 * later App Insights) read the relevant signal and only initialise once it's
 * granted. Persisted to localStorage so the choice survives reloads.
 */
@Injectable({ providedIn: 'root' })
export class ConsentService {
  private readonly state = signal<ConsentState>(this.read());

  /** Whether the user has chosen yet — drives the first-run banner. */
  readonly showBanner       = computed(() => !this.state().decided);
  readonly analyticsGranted = computed(() => this.state().analytics);
  readonly marketingGranted = computed(() => this.state().marketing);
  readonly snapshot         = computed(() => this.state());

  /** Preferences modal visibility (reopened from the footer or banner). */
  readonly prefsOpen = signal(false);

  acceptAll(): void { this.persist({ analytics: true,  marketing: true,  decided: true }); }
  rejectAll(): void { this.persist({ analytics: false, marketing: false, decided: true }); }

  /** Save explicit per-category choices (from the preferences modal). */
  save(prefs: { analytics: boolean; marketing: boolean }): void {
    this.persist({ analytics: prefs.analytics, marketing: prefs.marketing, decided: true });
    this.prefsOpen.set(false);
  }

  openPrefs():  void { this.prefsOpen.set(true); }
  closePrefs(): void { this.prefsOpen.set(false); }

  private persist(next: ConsentState): void {
    this.state.set(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* storage off — keep in-memory */ }
  }

  private read(): ConsentState {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const p = JSON.parse(raw) as Partial<ConsentState>;
        return { analytics: !!p.analytics, marketing: !!p.marketing, decided: !!p.decided };
      }
    } catch { /* unreadable — fall through to empty */ }
    return { ...EMPTY };
  }
}
